import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  Archive,
  ArchiveRestore,
  Film,
  GraduationCap,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { learningApi } from '../services/api'
import { useToast } from '../context/ToastContext'
import { usePageBreadcrumbs } from '../context/BreadcrumbContext'
import {
  ConfirmDialog,
  Modal,
  PanelState,
  cardCls,
  errorMessage,
  inputCls,
  primaryBtn,
  secondaryBtn,
} from '../components/opco/admin/shared'
import {
  VIDEO_ACCEPT,
  formatBytes,
  videoFileProblem,
} from '../utils/learning'

const STARTER = {
  category: 'Template',
  item: 'How to create a template and connect it to Boomi',
}

const iconBtn = 'inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-30 disabled:hover:bg-transparent dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
const labelCls = 'mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-300'
const textareaCls = 'w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-navy-500/40'

function Badge({ tone = 'gray', children }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    amber: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800',
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  )
}

function moved(list, index, delta) {
  const ids = list.map((x) => x.id)
  const target = index + delta
  if (target < 0 || target >= ids.length) return null
  ;[ids[index], ids[target]] = [ids[target], ids[index]]
  return ids
}

/* -------------------------------------------------------------------------- */
/* Forms                                                                      */
/* -------------------------------------------------------------------------- */

function CategoryForm({ category, onClose, onSaved }) {
  const toast = useToast()
  const [name, setName] = useState(category?.name || '')
  const [description, setDescription] = useState(category?.description || '')
  const [saving, setSaving] = useState(false)

  const save = async (e) => {
    e?.preventDefault()
    setSaving(true)
    try {
      if (category) await learningApi.updateCategory(category.id, { name, description })
      else await learningApi.createCategory({ name, description })
      onSaved()
    } catch (err) {
      toast.error('Could not save the category', errorMessage(err))
      setSaving(false)
    }
  }

  return (
    <Modal
      title={category ? 'Rename category' : 'New category'}
      onClose={saving ? undefined : onClose}
      labelledBy="learning-category-title"
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtn}>Cancel</button>
          <button type="button" onClick={save} disabled={saving || !name.trim()} className={primaryBtn}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </>
      )}
    >
      <form onSubmit={save} className="flex flex-col gap-3">
        <div>
          <label htmlFor="learning-category-name" className={labelCls}>Name</label>
          <input id="learning-category-name" className={inputCls} value={name} maxLength={200}
            onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div>
          <label htmlFor="learning-category-description" className={labelCls}>Description (optional)</label>
          <textarea id="learning-category-description" className={textareaCls} rows={2} maxLength={2000}
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
      </form>
    </Modal>
  )
}

function LinksEditor({ links, onChange }) {
  const update = (i, key, value) => onChange(links.map((l, j) => (j === i ? { ...l, [key]: value } : l)))
  return (
    <div className="flex flex-col gap-2">
      {links.map((link, i) => (
        <div key={i} className="flex gap-2">
          <input className={inputCls} placeholder="Label" aria-label={`Link ${i + 1} label`} value={link.label}
            onChange={(e) => update(i, 'label', e.target.value)} />
          <input className={inputCls} placeholder="https://" aria-label={`Link ${i + 1} URL`} value={link.url}
            onChange={(e) => update(i, 'url', e.target.value)} />
          <button type="button" className={iconBtn} aria-label={`Remove link ${i + 1}`}
            onClick={() => onChange(links.filter((_, j) => j !== i))}>
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...links, { label: '', url: '' }])}
        className="inline-flex w-fit items-center gap-1.5 text-xs font-semibold text-navy-700 hover:underline dark:text-navy-300">
        <Plus className="h-3.5 w-3.5" /> Add link
      </button>
    </div>
  )
}

function ItemForm({ item, categories, defaultCategoryId, onClose, onSaved }) {
  const toast = useToast()
  const [title, setTitle] = useState(item?.title || '')
  const [categoryId, setCategoryId] = useState(item?.category_id || defaultCategoryId || categories[0]?.id || '')
  const [description, setDescription] = useState(item?.description || '')
  const [steps, setSteps] = useState((item?.steps || []).join('\n'))
  const [links, setLinks] = useState(item?.links?.length ? item.links : [])
  const [file, setFile] = useState(null)
  const [progress, setProgress] = useState(null)
  const [saving, setSaving] = useState(false)
  const fileProblem = videoFileProblem(file)

  const save = async (e) => {
    e?.preventDefault()
    setSaving(true)
    const payload = {
      title,
      category_id: Number(categoryId),
      description,
      steps: steps.split('\n'),
      links: links.filter((l) => l.url.trim() || l.label.trim()),
    }
    let saved = item
    try {
      saved = item
        ? (await learningApi.updateItem(item.id, payload)).data
        : (await learningApi.createItem(payload)).data
    } catch (err) {
      toast.error('Could not save the guide', errorMessage(err))
      setSaving(false)
      return
    }
    if (file) {
      try {
        setProgress(0)
        await learningApi.uploadVideo(saved.id, file, setProgress)
      } catch (err) {
        toast.error('The guide was saved but the video did not upload', errorMessage(err))
        setSaving(false)
        setProgress(null)
        onSaved({ keepOpen: true, item: saved })
        return
      }
    }
    toast.success(item ? 'Guide updated' : 'Guide created')
    onSaved()
  }

  return (
    <Modal
      title={item ? 'Edit guide' : 'New guide'}
      onClose={saving ? undefined : onClose}
      labelledBy="learning-item-title"
      wide
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtn}>Cancel</button>
          <button type="button" onClick={save} disabled={saving || !title.trim() || !categoryId || Boolean(fileProblem)}
            className={primaryBtn}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {progress !== null ? `Uploading ${progress}%` : 'Save'}
          </button>
        </>
      )}
    >
      <form onSubmit={save} className="flex max-h-[65vh] flex-col gap-4 overflow-y-auto pr-1">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="learning-item-title-input" className={labelCls}>Title</label>
            <input id="learning-item-title-input" className={inputCls} value={title} maxLength={300}
              onChange={(e) => setTitle(e.target.value)} autoFocus />
          </div>
          <div>
            <label htmlFor="learning-item-category" className={labelCls}>Category</label>
            <select id="learning-item-category" className={inputCls} value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="learning-item-description" className={labelCls}>Description (optional)</label>
          <textarea id="learning-item-description" className={textareaCls} rows={2} maxLength={4000}
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <label htmlFor="learning-item-steps" className={labelCls}>Step-by-step instructions (one step per line, optional)</label>
          <textarea id="learning-item-steps" className={textareaCls} rows={5}
            value={steps} onChange={(e) => setSteps(e.target.value)} />
        </div>
        <div>
          <span className={labelCls}>Supporting links (optional)</span>
          <LinksEditor links={links} onChange={setLinks} />
        </div>
        <div>
          <label htmlFor="learning-item-video" className={labelCls}>
            {item?.has_video ? 'Replace video' : 'Video'} (.mp4, .webm or .mov, up to 250 MB)
          </label>
          {item?.has_video && !file && (
            <p className="m-0 mb-2 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <Film className="h-3.5 w-3.5" /> Current video: {formatBytes(item.video_size_bytes)}
            </p>
          )}
          <input id="learning-item-video" type="file" accept={VIDEO_ACCEPT} disabled={saving}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200 dark:text-gray-300 dark:file:bg-gray-800 dark:file:text-gray-200" />
          {fileProblem && <p className="m-0 mt-1 text-xs text-red-600 dark:text-red-400" role="alert">{fileProblem}</p>}
          {progress !== null && (
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800" role="progressbar"
              aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Upload progress">
              <div className="h-full bg-navy-700 transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          {!item?.has_video && !file && (
            <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400">
              A guide stays hidden from users until it has a video.
            </p>
          )}
        </div>
      </form>
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/* Lists                                                                      */
/* -------------------------------------------------------------------------- */

function ItemRow({ item, index, count, busy, onMove, onEdit, onArchive, onDelete }) {
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <Film className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {!item.has_video && <Badge tone="amber">Draft - no video, hidden</Badge>}
          {item.has_video && <Badge>{formatBytes(item.video_size_bytes)}</Badge>}
        </div>
      </div>
      <div className="flex items-center gap-0.5">
        <button type="button" className={iconBtn} disabled={busy || index === 0} onClick={() => onMove(-1)}
          aria-label={`Move ${item.title} up`}><ArrowUp className="h-4 w-4" /></button>
        <button type="button" className={iconBtn} disabled={busy || index === count - 1} onClick={() => onMove(1)}
          aria-label={`Move ${item.title} down`}><ArrowDown className="h-4 w-4" /></button>
        <button type="button" className={iconBtn} disabled={busy} onClick={onEdit}
          aria-label={`Edit ${item.title}`}><Pencil className="h-4 w-4" /></button>
        <button type="button" className={iconBtn} disabled={busy} onClick={onArchive}
          aria-label={`Archive ${item.title}`}><Archive className="h-4 w-4" /></button>
        <button type="button" className={iconBtn} disabled={busy} onClick={onDelete}
          aria-label={`Delete ${item.title}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </li>
  )
}

// category.items here are the active (not archived) guides only; archived ones
// are listed in the Archived tab.
function CategoryCard({ category, index, count, busy, actions }) {
  const archivedCount = category.archivedCount || 0
  return (
    <section className={cardCls} aria-label={category.name}>
      <header className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-sm font-semibold text-gray-900 dark:text-gray-100">{category.name}</h2>
          <p className="m-0 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {category.items.length} {category.items.length === 1 ? 'guide' : 'guides'}
            {archivedCount > 0 && ` (+${archivedCount} archived)`}
          </p>
        </div>
        <div className="flex items-center gap-0.5">
          <button type="button" className={iconBtn} disabled={busy || index === 0}
            onClick={() => actions.moveCategory(index, -1)} aria-label={`Move ${category.name} up`}>
            <ArrowUp className="h-4 w-4" /></button>
          <button type="button" className={iconBtn} disabled={busy || index === count - 1}
            onClick={() => actions.moveCategory(index, 1)} aria-label={`Move ${category.name} down`}>
            <ArrowDown className="h-4 w-4" /></button>
          <button type="button" className={iconBtn} disabled={busy}
            onClick={() => actions.renameCategory(category)} aria-label={`Rename ${category.name}`}>
            <Pencil className="h-4 w-4" /></button>
          <button type="button" className={iconBtn} disabled={busy}
            onClick={() => actions.deleteCategory(category)} aria-label={`Delete ${category.name}`}>
            <Trash2 className="h-4 w-4" /></button>
          <button type="button" className={`${secondaryBtn} ml-2 h-8 px-3`} disabled={busy}
            onClick={() => actions.newItem(category)}>
            <Plus className="h-4 w-4" /> Add guide
          </button>
        </div>
      </header>
      {category.items.length === 0 ? (
        <p className="m-0 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">No guides in this category yet.</p>
      ) : (
        <ul className="m-0 list-none divide-y divide-gray-100 p-0 dark:divide-gray-800">
          {category.items.map((item, i) => (
            <ItemRow
              key={item.id}
              item={item}
              index={i}
              count={category.items.length}
              busy={busy}
              onMove={(delta) => actions.moveItem(category, i, delta)}
              onEdit={() => actions.editItem(item)}
              onArchive={() => actions.archiveItem(item)}
              onDelete={() => actions.deleteItem(item)}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function ArchivedList({ items, busy, actions }) {
  if (items.length === 0) {
    return (
      <div className={`${cardCls} px-6 py-10 text-center`}>
        <p className="m-0 text-sm text-gray-600 dark:text-gray-300">No archived guides.</p>
        <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400">
          Archive a guide to hide it from users without deleting it.
        </p>
      </div>
    )
  }
  return (
    <section className={cardCls} aria-label="Archived guides">
      <ul className="m-0 list-none divide-y divide-gray-100 p-0 dark:divide-gray-800">
        {items.map(({ item, category }) => (
          <li key={item.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <Archive className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="m-0 truncate text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge>{category.name}</Badge>
                {!item.has_video && <Badge tone="amber">No video</Badge>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className={`${secondaryBtn} h-8 px-3`} disabled={busy}
                onClick={() => actions.restoreItem(item)} aria-label={`Restore ${item.title}`}>
                <ArchiveRestore className="h-4 w-4" /> Restore
              </button>
              <button type="button" className={iconBtn} disabled={busy}
                onClick={() => actions.deleteItem(item)} aria-label={`Delete ${item.title}`}>
                <Trash2 className="h-4 w-4" /></button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

function Tabs({ tab, onChange, archivedCount }) {
  const tabs = [
    { id: 'guides', label: 'Guides' },
    { id: 'archived', label: `Archived (${archivedCount})` },
  ]
  return (
    <div role="tablist" aria-label="Learning content" className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          onClick={() => onChange(t.id)}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            tab === t.id
              ? 'border-navy-700 text-navy-800 dark:border-navy-300 dark:text-navy-100'
              : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function LearningManage() {
  const toast = useToast()
  const [access, setAccess] = useState('loading') // loading | editor | denied | unavailable
  const [categories, setCategories] = useState([])
  const [tab, setTab] = useState('guides') // guides | archived
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [categoryForm, setCategoryForm] = useState(null) // { category } | { category: null }
  const [itemForm, setItemForm] = useState(null) // { item, categoryId }
  const [confirm, setConfirm] = useState(null) // { title, message, run }

  usePageBreadcrumbs([{ label: 'Manage' }])

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await learningApi.adminCatalog()
      setCategories(res.data?.categories || [])
    } catch (err) {
      setError(errorMessage(err, 'Could not load the Learning content'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    learningApi.adminMe()
      .then((res) => {
        if (cancelled) return
        if (res?.data?.editor) {
          setAccess('editor')
          load()
        } else {
          setAccess('denied')
        }
      })
      .catch((err) => { if (!cancelled) setAccess(err?.response?.status === 404 ? 'unavailable' : 'denied') })
    return () => { cancelled = true }
  }, [load])

  const run = async (fn, failure) => {
    setBusy(true)
    try {
      await fn()
      await load()
    } catch (err) {
      toast.error(failure, errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  // Active categories list only guides that are not archived (drafts without
  // a video included); every archived guide is in the Archived tab instead.
  const activeCategories = useMemo(() => categories.map((category) => {
    const items = category.items || []
    return {
      ...category,
      items: items.filter((item) => !item.is_archived),
      archivedCount: items.filter((item) => item.is_archived).length,
    }
  }), [categories])
  const archivedItems = useMemo(() => categories.flatMap((category) => (category.items || [])
    .filter((item) => item.is_archived)
    .map((item) => ({ item, category }))), [categories])

  const actions = {
    moveCategory: (index, delta) => {
      const ids = moved(activeCategories, index, delta)
      if (ids) run(() => learningApi.reorderCategories(ids), 'Could not reorder categories')
    },
    renameCategory: (category) => setCategoryForm({ category }),
    deleteCategory: (category) => {
      const total = category.items.length + category.archivedCount
      setConfirm({
        title: 'Delete category',
        message: total
          ? `"${category.name}" still has ${total} guide(s)${category.archivedCount ? `, ${category.archivedCount} of them archived` : ''}. Move or delete them first.`
          : `Delete "${category.name}"? This can't be undone.`,
        blocked: total > 0,
        run: () => learningApi.deleteCategory(category.id),
        failure: 'Could not delete the category',
      })
    },
    newItem: (category) => setItemForm({ item: null, categoryId: category.id }),
    editItem: (item) => setItemForm({ item, categoryId: item.category_id }),
    moveItem: (category, index, delta) => {
      const ids = moved(category.items, index, delta)
      if (ids) run(() => learningApi.reorderItems(category.id, ids), 'Could not reorder guides')
    },
    archiveItem: (item) => run(
      () => learningApi.archiveItem(item.id, true),
      'Could not archive the guide',
    ),
    restoreItem: (item) => run(
      () => learningApi.archiveItem(item.id, false),
      'Could not restore the guide',
    ),
    deleteItem: (item) => setConfirm({
      title: 'Delete guide',
      message: item.is_archived
        ? `Delete "${item.title}" and its video? This can't be undone.`
        : `Delete "${item.title}" and its video? This can't be undone. Archive it instead to hide it but keep it.`,
      run: () => learningApi.deleteItem(item.id),
      failure: 'Could not delete the guide',
    }),
  }

  const addStarter = () => run(async () => {
    const category = (await learningApi.createCategory({ name: STARTER.category })).data
    const item = (await learningApi.createItem({
      category_id: category.id, title: STARTER.item,
    })).data
    setItemForm({ item, categoryId: category.id })
  }, 'Could not add the starter content')

  let body
  if (access === 'loading') {
    body = <PanelState loading />
  } else if (access === 'unavailable') {
    body = (
      <p className="m-0 rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
        Learning content is managed on the Xtract hub, not on this deployment.
      </p>
    )
  } else if (access === 'denied') {
    body = (
      <p className="m-0 rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300" role="alert">
        You don't have access to manage Learning content.
      </p>
    )
  } else {
    body = (
      <PanelState loading={loading} error={error} onRetry={load}>
        {categories.length === 0 ? (
          <div className={`${cardCls} flex flex-col items-center gap-3 px-6 py-10 text-center`}>
            <p className="m-0 text-sm text-gray-600 dark:text-gray-300">No categories yet.</p>
            <button type="button" className={secondaryBtn} disabled={busy} onClick={addStarter}>
              <Plus className="h-4 w-4" /> Add starter content ({STARTER.category} &rsaquo; {STARTER.item})
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <Tabs tab={tab} onChange={setTab} archivedCount={archivedItems.length} />
            {tab === 'archived' ? (
              <ArchivedList items={archivedItems} busy={busy} actions={actions} />
            ) : (
              activeCategories.map((category, i) => (
                <CategoryCard key={category.id} category={category} index={i} count={activeCategories.length}
                  busy={busy} actions={actions} />
              ))
            )}
          </div>
        )}
      </PanelState>
    )
  }

  return (
    <div className="animate-page-in flex max-w-[1000px] flex-col gap-5">
      <header className="animate-header-in flex flex-wrap items-center gap-3.5">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm">
          <GraduationCap className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="m-0 text-2xl font-bold leading-[30px] tracking-tight text-gray-900 dark:text-gray-100">
            Manage Learning
          </h1>
          <p className="m-0 mt-1 text-sm text-gray-500 dark:text-gray-400">
            Changes here appear in every Xtract deployment, for everyone. Archived guides, guides without a video and categories with nothing to watch are hidden from users.
          </p>
        </div>
        <Link to="/learning" className={secondaryBtn}>
          <ArrowLeft className="h-4 w-4" /> Back to Learning
        </Link>
        {access === 'editor' && (
          <button type="button" className={primaryBtn} disabled={busy} onClick={() => setCategoryForm({ category: null })}>
            <Plus className="h-4 w-4" /> New category
          </button>
        )}
      </header>

      {body}

      {categoryForm && (
        <CategoryForm
          category={categoryForm.category}
          onClose={() => setCategoryForm(null)}
          onSaved={() => { setCategoryForm(null); load() }}
        />
      )}
      {itemForm && (
        <ItemForm
          key={itemForm.item?.id || 'new'}
          item={itemForm.item}
          categories={categories}
          defaultCategoryId={itemForm.categoryId}
          onClose={() => { setItemForm(null); load() }}
          onSaved={(opts) => {
            if (opts?.keepOpen) setItemForm({ item: opts.item, categoryId: opts.item.category_id })
            else setItemForm(null)
            load()
          }}
        />
      )}
      {confirm && (
        confirm.blocked ? (
          <Modal title={confirm.title} onClose={() => setConfirm(null)} labelledBy="learning-blocked-title"
            footer={<button type="button" className={secondaryBtn} onClick={() => setConfirm(null)}>OK</button>}>
            <p className="m-0 text-sm text-gray-600 dark:text-gray-300">{confirm.message}</p>
          </Modal>
        ) : (
          <ConfirmDialog
            title={confirm.title}
            message={confirm.message}
            confirmLabel="Delete"
            danger
            busy={busy}
            onCancel={() => setConfirm(null)}
            onConfirm={async () => {
              const { run: fn, failure } = confirm
              await run(fn, failure)
              setConfirm(null)
            }}
          />
        )
      )}
      {busy && <span className="sr-only" role="status">Saving</span>}
    </div>
  )
}
